<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 bg-black bg-opacity-50 px-6 flex items-center justify-center z-50"
    @click.self="closeModal"
  >
    <div
      class="bg-white rounded-lg shadow-lg p-6 max-w-3xl w-full relative max-h-[90vh] overflow-y-auto"
    >
      <button
        class="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        @click="closeModal"
      >
        ✕
      </button>
      <div
        v-if="isLoading"
        class="flex flex-col justify-center items-center min-h-[400px]"
      >
        <div
          class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-purple mb-6"
        ></div>
      </div>
      <div v-else class="flex flex-col gap-6">
        <div class="flex flex-col items-center">
          <span class="text-xl font-bold text-gray-900"
            >Subscription Details</span
          >
        </div>
        <div v-if="upcomingInvoice" class="border-t border-gray-200 my-0"></div>
        <div
          v-if="isLoading"
          class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <!-- Spinner -->
          <div
            class="animate-spin rounded-full h-32 w-32 border-t-4 border-b-4 border-primary-purple"
          ></div>
        </div>
        <div v-if="upcomingInvoice" class="flex flex-col">
          <span class="text-lg font-semibold text-gray-900 text-left mb-4"
            >Upcoming Invoice</span
          >
          <div>
            <div class="max-h-[350px] overflow-y-auto">
              <div class="overflow-x-auto mr-8">
                <table
                  class="w-full min-w-[700px] border border-gray-200 mb-4 mx-auto"
                >
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        class="pl-4 py-2 text-left text-xs font-semibold text-gray-500"
                      >
                        Description
                      </th>
                      <th
                        class="pl-2 py-2 text-right text-xs font-semibold text-gray-500 w-20"
                      >
                        Qty
                      </th>
                      <th
                        class="px-0 py-2 text-right text-xs font-semibold text-gray-500 w-28"
                      >
                        Unit price
                      </th>
                      <th
                        class="px-0 py-2 text-right text-xs font-semibold text-gray-500 w-28"
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template
                      v-for="(items, period) in groupedLineItems"
                      :key="period"
                    >
                      <tr>
                        <td
                          class="py-2 text-sm text-gray-900 text-left pl-4 font-semibold"
                          colspan="4"
                        >
                          {{ period }}
                        </td>
                      </tr>
                      <tr v-for="(item, idx) in items" :key="idx">
                        <td class="py-2 text-sm text-gray-900 text-left pl-4">
                          {{ item.description }}
                        </td>
                        <td
                          class="py-2 text-right text-sm text-gray-900 pl-2 w-20"
                        >
                          {{ item.qty }}
                        </td>
                        <td
                          class="py-2 text-right text-sm text-gray-900 px-0 w-28"
                        >
                          ${{
                            item.unitPrice.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td
                          class="py-2 text-right text-sm text-gray-900 px-0 w-28"
                        >
                          ${{
                            item.amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          }}
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
              <div class="w-full max-w-3xl mx-auto mt-4">
                <table class="w-fit ml-auto text-sm">
                  <thead class="sr-only">
                    <tr>
                      <th>Label</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        class="text-gray-700 text-right pr-8 py-1 whitespace-nowrap"
                      >
                        Subtotal
                      </td>
                      <td class="text-gray-900 text-left py-1">
                        ${{
                          upcomingInvoice.subtotal.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        }}
                      </td>
                    </tr>
                    <tr
                      v-if="
                        upcomingInvoice.discounts &&
                        upcomingInvoice.discounts.length
                      "
                    >
                      <td
                        class="text-gray-700 text-right pr-8 py-1 whitespace-nowrap"
                      >
                        {{ upcomingInvoice.discounts[0].label }}
                      </td>
                      <td class="text-green-700 text-left py-1">
                        -${{
                          upcomingInvoice.discounts[0].amount.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="text-gray-700 text-right pr-8 py-1 whitespace-nowrap"
                      >
                        Total excluding tax
                      </td>
                      <td class="text-gray-900 text-left py-1">
                        ${{
                          upcomingInvoice.totalExcludingTax.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="text-gray-700 text-right pr-8 py-1 whitespace-nowrap"
                      >
                        Tax
                      </td>
                      <td class="text-gray-900 text-left py-1">
                        <span
                          v-if="
                            !upcomingInvoice.tax || upcomingInvoice.tax === 0
                          "
                          >-</span
                        >
                        <span v-else>
                          ${{
                            upcomingInvoice.tax.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="text-gray-900 text-right pr-8 py-1 whitespace-nowrap"
                      >
                        Total
                      </td>
                      <td class="text-gray-900 text-left py-1">
                        ${{
                          upcomingInvoice.total.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="text-gray-900 text-right pr-8 py-1 whitespace-nowrap"
                      >
                        Applied Balance
                      </td>
                      <td class="text-gray-900 text-left py-1">
                        ${{
                          upcomingInvoice.appliedBalance.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="text-gray-900 text-right pr-8 py-1 whitespace-nowrap font-semibold"
                      >
                        Amount due
                      </td>
                      <td class="text-gray-900 text-left py-1 font-semibold">
                        ${{
                          upcomingInvoice.amountDue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="border-t border-gray-200 my-1"></div>
        <CurrentPaymentMethodModal
          :source="source"
          :cardNumber="cardNumber"
          :cardExpiry="cardExpiry"
          :invoiceRecipientEmail="invoiceRecipientEmail"
        />
        <div class="border-t border-gray-200 my-0"></div>
        <div class="flex flex-col gap-2 mt-4">
          <BaseButton
            type="button"
            class="flex flex-col justify-center items-center p-2 w-full text-white bg-primary-purple rounded-lg text-sm font-medium leading-none text-center"
            @click="$emit('proceed')"
            ><span>Proceed</span></BaseButton
          >
          <BaseButton
            v-if="showBillingButton"
            type="button"
            class="flex flex-col justify-center items-center p-2 w-full text-primary-purple bg-violet-100 rounded-lg text-sm font-medium leading-none text-center"
            @click="goToBilling"
            ><span>Change Payment Method in Billing</span></BaseButton
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from "vue";
import BaseButton from "@/components/shared/BaseButton.vue";
import CurrentPaymentMethodModal from "../payment-method/CurrentPaymentMethodModal.vue";
import router from "@/router";
import {
  CARD_ICON,
  DOCUMENT_DOLLAR_ICON,
} from "@/assets/svg/shared/svg-constants";

interface UpcomingInvoice {
  groupedLineItems?: Record<string, any[]>;
  subtotal: number;
  discounts?: Array<{ label: string; amount: number }>;
  totalExcludingTax: number;
  tax?: number;
  total: number;
  amountDue: number;
  appliedBalance: number;
}

export default defineComponent({
  name: "SubscriptionDetailsModal",
  components: { BaseButton, CurrentPaymentMethodModal },
  props: {
    isVisible: { type: Boolean, required: true },
    source: { type: String, required: true },
    cardNumber: { type: String as PropType<string | null>, default: null },
    cardExpiry: { type: String as PropType<string | null>, default: null },
    invoiceRecipientEmail: {
      type: String as PropType<string | null>,
      default: null,
    },
    showBillingButton: { type: Boolean, required: true },
    upcomingInvoice: Object as PropType<UpcomingInvoice | null>,
    isLoading: { type: Boolean, default: false },
  },
  emits: ["proceed", "update:isVisible"],
  setup(props, { emit }) {
    const closeModal = () => {
      emit("update:isVisible", false);
    };

    const goToBilling = () => {
      router.push({ name: "BillingPage" });
    };

    const groupedLineItems = computed<Record<string, any[]>>(() => {
      const groups =
        (props.upcomingInvoice && props.upcomingInvoice.groupedLineItems) || {};
      const mapped: Record<string, any[]> = {};
      for (const [period, items] of Object.entries(groups)) {
        mapped[period] = items.map((item: any) => ({
          ...item,
          qty: item.quantity,
          unitPrice: item.unit_price,
        }));
      }
      return mapped;
    });

    return {
      closeModal,
      goToBilling,
      CARD_ICON,
      DOCUMENT_DOLLAR_ICON,
      groupedLineItems,
    };
  },
});
</script>
